/**
 * Spec Panel — Master-detail layout for consumed Model Specs.
 *
 * Right-side slide-in panel for the PRODUCTION (build) workspace, mirroring
 * UnifiedFindingsPanel's structure: list always visible, detail to the right,
 * parent controls the panel width via wrapper div.
 *
 * Groups = consumed specs (one per upstream research workspace). Group header
 * carries the source title, the human sign-off badge (signed / unsigned /
 * approval superseded) and THIS build's progress bar — two builds consuming
 * the same spec keep independent scorecards by design.
 * Cards = requirements (REQ id, statement, done / open / done-stale, evidence
 * count, implementing node when done). Detail pane = full statement, grounding
 * finding chips (click-through to FindingDetailView when the Finding is loaded
 * in the store), impl note and the validator's verdict.
 *
 * Data = the joined projection from GET /projects/{pid}/workspaces/{ws}/
 * requirements (`specs` array). Refresh rides the existing
 * `requirements_updated` workspace-op channel via the `refreshSignal` prop
 * (same pattern as WorkspaceDetailsOverlay).
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { formatDateTime } from '../../utils/formatDateTime';
import {
  X,
  ArrowLeft,
  AlertTriangle,
  CheckSquare,
  Square,
  Paperclip,
  PenLine,
  ListChecks,
  RefreshCw,
} from 'lucide-react';
import api, { approveSpec, SpecResolutionError } from '../../apiService';
import type {
  ConsumedSpec,
  SpecFindingRef,
  SpecRequirementItem,
} from '../../apiService';
import FindingDetailView from '../workspace/FindingDetailView';
import { useProjectStore } from '../../stores/projectStore';
import type { DictFinding } from '../../types/workspaceState';
import { formatCount } from '../../utils/plural';

// =============================================================================
// TYPES
// =============================================================================

export interface SpecPanelSummary {
  specCount: number;
  openCount: number;
}

interface SpecPanelProps {
  /** Current project id — requirements fetch scope. */
  projectId: string;
  /** The consuming BUILD workspace id (not the authoring research one). */
  workspaceId: string;
  /** Firebase ID token for the orchestrator calls. */
  authToken: string;
  isOpen: boolean;
  onClose: () => void;
  /** Bumped by the parent on `requirements_updated` workspace ops → re-fetch. */
  refreshSignal?: number;
  /** Controlled selection: open the panel focused on this requirement.
   *  Composite key `${source_workspace_id}::${requirement_id}` — must match
   *  reqKey(). Absent → fully uncontrolled (existing behavior). */
  focusRequirementKey?: string | null;
  /** Bump to re-apply focusRequirementKey on repeat clicks of the same key
   *  (the key alone is value-gated by React). */
  focusNonce?: number;
  /** Cross-pane click-through: select the implementing node in the canvas. */
  onNavigateToNode?: (nodeId: string) => void;
  onDetailOpenChange?: (isDetailOpen: boolean) => void;
  /** Left-edge resize — incremental pixel deltas, parent owns the width. */
  onPanelResize?: (delta: number) => void;
  /** Reports {specCount, openCount} after each fetch so the parent can
   *  render the toggle badge without duplicating the fetch. */
  onSummaryChange?: (summary: SpecPanelSummary) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Composite requirement key — a requirement is NEVER addressed bare. */
export function reqKey(sourceWorkspaceId: string, requirementId: string): string {
  return `${sourceWorkspaceId}::${requirementId}`;
}

function formatTimestamp(at?: string | null): string {
  if (!at) return '';
  return formatDateTime(at, { invalidFallback: at });
}

/** Short evidence-chip label from a resolved finding, else the raw id. */
function findingChipLabel(ref: SpecFindingRef, resolved: DictFinding | null): string {
  if (resolved) {
    return (
      resolved.source_reference
      || resolved.source_document_celex
      || resolved.source_document_name
      || resolved.finding_id
    );
  }
  return ref.finding_id;
}

// =============================================================================
// TOGGLE BUTTON (exported for parent components to render)
// =============================================================================

export function SpecToggleButton({
  openCount, onToggle,
}: {
  /** Requirements still open across all consumed specs (badge). */
  openCount: number;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="absolute top-16 right-4 z-20 p-2 rounded-lg shadow-lg border transition-all bg-surface/90 backdrop-blur-md text-fg-secondary border-border-default/50 hover:bg-surface"
      title={`Open spec panel (${formatCount(openCount, { one: 'open requirement', other: 'open requirements' })})`}
    >
      <ListChecks size={20} />
      {openCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-accent text-white text-xs min-w-[20px] h-5 rounded-full flex items-center justify-center px-1 font-medium">
          {openCount}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// SIGN-OFF BADGE + PROGRESS BAR
// =============================================================================

function SignOffBadge({ spec }: { spec: ConsumedSpec }) {
  if (spec.approval && spec.approval_current) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-success-bg text-success font-medium flex-shrink-0"
        title={`Signed off by ${spec.approval.approved_by || 'unknown'}${spec.approval.approved_at ? ` at ${formatTimestamp(spec.approval.approved_at)}` : ''}`}
      >
        <PenLine size={9} strokeWidth={1.5} />
        Signed
      </span>
    );
  }
  if (spec.approval && !spec.approval_current) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-warning-bg text-warning font-medium flex-shrink-0"
        title="The spec changed after sign-off — re-approve to sign the current version"
      >
        <AlertTriangle size={9} strokeWidth={1.5} />
        Superseded
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-warning-bg text-warning font-medium flex-shrink-0"
      title="No human sign-off — the build is working against a draft spec"
    >
      <AlertTriangle size={9} strokeWidth={1.5} />
      Unsigned
    </span>
  );
}

function SpecProgressBar({ progress }: { progress: ConsumedSpec['progress'] }) {
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <div className="flex items-center gap-1.5 min-w-0 flex-1">
      <div className="h-1 flex-1 min-w-[24px] rounded-full bg-bg-warm overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[9px] text-fg-tertiary flex-shrink-0 tabular-nums">
        {progress.done}/{progress.total}
      </span>
      {progress.stale > 0 && (
        <span
          className="text-[9px] text-warning flex-shrink-0 inline-flex items-center gap-0.5"
          title={`${formatCount(progress.stale, { one: 'requirement', other: 'requirements' })} marked done before the spec changed`}
        >
          <AlertTriangle size={9} strokeWidth={1.5} />
          {progress.stale}
        </span>
      )}
    </div>
  );
}

// =============================================================================
// COMPACT REQUIREMENT CARD
// =============================================================================

function RequirementCard({
  req, isSelected, onClick,
}: {
  req: SpecRequirementItem; isSelected: boolean; onClick: () => void;
}) {
  const done = !!req.status?.done;
  const stale = done && !!req.status?.stale;
  const evidenceCount = (req.finding_refs || []).length;

  const statusIcon = stale ? (
    <AlertTriangle size={11} className="text-warning flex-shrink-0" strokeWidth={1.5} />
  ) : done ? (
    <CheckSquare size={11} className="text-success flex-shrink-0" strokeWidth={1.5} />
  ) : (
    <Square size={11} className="text-fg-tertiary flex-shrink-0" strokeWidth={1.5} />
  );

  const statusLabel = req.withdrawn
    ? 'withdrawn'
    : stale ? 'done (stale — spec changed)' : done ? 'done' : 'open';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-1.5 py-1 rounded transition-all group ${
        isSelected
          ? 'bg-accent-tint border border-accent-border'
          : stale
            ? 'hover:bg-warning-bg/50 border border-warning/50'
            : 'hover:bg-bg-warm border border-transparent'
      }`}
      title={`${req.requirement_id} — ${statusLabel}\n${req.statement}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {statusIcon}
        <span className="font-mono text-[10px] text-fg-tertiary flex-shrink-0">
          {req.requirement_id}
        </span>
        <span
          className={`truncate leading-tight text-[11px] flex-1 min-w-0 ${
            req.withdrawn
              ? 'text-fg-tertiary line-through'
              : done ? 'text-fg-secondary' : 'text-fg font-medium'
          }`}
        >
          {req.statement}
        </span>
        {evidenceCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[9px] text-fg-tertiary flex-shrink-0">
            <Paperclip size={9} strokeWidth={1.5} />
            {evidenceCount}
          </span>
        )}
        {done && req.status?.node_id && (
          <span
            className="font-mono text-[9px] text-fg-tertiary flex-shrink-0 truncate max-w-[72px]"
            title={`Implemented by node ${req.status.node_id}`}
          >
            →{req.status.node_id}
          </span>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// SPEC GROUP (one consumed spec = one upstream research workspace)
// =============================================================================

export function SpecGroup({
  spec, selectedKey, approving, onRequirementClick, onApprove,
}: {
  spec: ConsumedSpec;
  selectedKey: string | null;
  approving: boolean;
  onRequirementClick: (key: string) => void;
  onApprove: (sourceWorkspaceId: string) => void;
}) {
  const [showOthers, setShowOthers] = useState(false);
  const approvedAndCurrent = !!spec.approval && spec.approval_current;
  const isPending = !!spec.pending || spec.sections.length === 0;
  if (isPending) {
    // Source is wired in the plan but has not committed requirements yet —
    // say so instead of rendering an empty group (or hiding the source).
    return (
      <div className="mb-2">
        <div className="px-1 py-1 mb-0.5 border-b border-border-default flex items-center gap-1.5 min-w-0">
          <span
            className="font-semibold text-fg-secondary uppercase tracking-wide text-[10px] truncate flex-1 min-w-0"
            title={`${spec.source_title || spec.source_workspace_id} (${spec.source_workspace_id})`}
          >
            {spec.source_title || spec.source_workspace_id}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-warm text-fg-tertiary flex-shrink-0">
            pending
          </span>
        </div>
        <p className="px-2 py-1.5 text-[11px] text-fg-tertiary leading-snug">
          No requirements committed yet — they appear here when this research
          workspace commits its spec (the <code className="text-[10px]">[REQ-NNN]</code> checklist
          in its report).
        </p>
      </div>
    );
  }
  // Consumer-routed projection: lead with what is routed to THIS workspace
  // (or unrouted → shown-to-all → routed_to_me true), and DEMOTE — never hide —
  // requirements meant only for other consumers into a collapsed group. With
  // no routing info (legacy specs), routed_to_me is undefined everywhere, so
  // `mine` = all and `others` is empty — identical to the prior behaviour.
  const routing = spec.routing;
  const showRoutingLine =
    !!routing && (routing.for_others_only > 0 || routing.unrouted > 0);
  const otherReqs: { section: string; req: SpecRequirementItem }[] = [];
  spec.sections.forEach((s) =>
    s.requirements.forEach((req) => {
      if (req.routed_to_me === false) otherReqs.push({ section: s.section, req });
    }),
  );
  return (
    <div className="mb-2">
      <div className="px-1 py-1 mb-0.5 border-b border-border-default">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className="font-semibold text-fg-secondary uppercase tracking-wide text-[10px] truncate flex-1 min-w-0"
            title={`${spec.source_title || spec.source_workspace_id} (${spec.source_workspace_id})`}
          >
            {spec.source_title || spec.source_workspace_id}
          </span>
          <SignOffBadge spec={spec} />
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <SpecProgressBar progress={spec.progress} />
          <button
            onClick={() => onApprove(spec.source_workspace_id)}
            disabled={approving || approvedAndCurrent}
            className="text-[9px] px-1.5 py-0.5 rounded border border-border-default text-fg-secondary hover:bg-bg-warm hover:border-border-strong font-medium transition-colors disabled:opacity-50 disabled:cursor-default flex-shrink-0"
            title={
              approvedAndCurrent
                ? 'Already signed off at the current spec version'
                : spec.approval
                  ? 'Sign off the current spec version (previous approval was superseded)'
                  : 'Sign off this spec version (human-only)'
            }
          >
            {approving
              ? 'Approving…'
              : approvedAndCurrent
                ? 'Approved'
                : spec.approval ? 'Re-approve' : 'Approve'}
          </button>
        </div>
        {showRoutingLine && (
          <div className="mt-1 text-[9px] text-fg-tertiary">
            {routing!.routed_to_me} routed to you
            {routing!.unrouted ? ` · ${routing!.unrouted} unrouted (shown to all)` : ''}
            {routing!.for_others_only ? ` · ${routing!.for_others_only} for other consumers` : ''}
          </div>
        )}
      </div>
      {spec.sections.map((section) => {
        const mine = section.requirements.filter((req) => req.routed_to_me !== false);
        if (mine.length === 0) return null;
        return (
          <div key={section.section || '(unsectioned)'} className="mb-1.5">
            {section.section && (
              <div className="px-1 pt-0.5 text-[9px] uppercase tracking-wide text-fg-tertiary">
                {section.section}
              </div>
            )}
            <ul className="space-y-0.5 pl-1 ml-1">
              {mine.map((req) => {
                const key = reqKey(spec.source_workspace_id, req.requirement_id);
                return (
                  <li key={key} className="flex items-start gap-1">
                    <RequirementCard
                      req={req}
                      isSelected={selectedKey === key}
                      onClick={() => onRequirementClick(key)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {otherReqs.length > 0 && (
        <div className="mt-1 pl-1 ml-1">
          <button
            onClick={() => setShowOthers((v) => !v)}
            className="text-[9px] text-fg-tertiary hover:text-fg-secondary transition-colors"
            title="Requirements this spec routes only to other consumers"
          >
            {showOthers ? '▾' : '▸'} {otherReqs.length} for other consumers
          </button>
          {showOthers && (
            <ul className="space-y-0.5 mt-0.5 opacity-60">
              {otherReqs.map(({ req }) => {
                const key = reqKey(spec.source_workspace_id, req.requirement_id);
                return (
                  <li key={key} className="flex items-start gap-1">
                    <RequirementCard
                      req={req}
                      isSelected={selectedKey === key}
                      onClick={() => onRequirementClick(key)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// REQUIREMENT DETAIL (right pane)
// =============================================================================

export function RequirementDetail({
  spec, section, req, allFindings, onFindingChipClick, onNavigateToNode,
}: {
  spec: ConsumedSpec;
  section: string;
  req: SpecRequirementItem;
  allFindings: Record<string, DictFinding>;
  onFindingChipClick: (findingId: string) => void;
  onNavigateToNode?: (nodeId: string) => void;
}) {
  const done = !!req.status?.done;
  const stale = done && !!req.status?.stale;

  const verdict = req.validation_verdict;
  const verdictClass = verdict === 'approved'
    ? 'bg-success-bg text-success'
    : verdict === 'flagged'
      ? 'bg-warning-bg text-warning'
      : verdict === 'rejected'
        ? 'bg-danger-bg text-danger'
        : 'bg-bg-warm text-fg-tertiary';

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Identity + statement */}
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-mono text-[11px] text-fg-tertiary">{req.requirement_id}</span>
          {section && (
            <span className="text-[10px] uppercase tracking-wide text-fg-tertiary">{section}</span>
          )}
          {req.withdrawn && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-warm text-fg-tertiary font-medium uppercase">
              Withdrawn
            </span>
          )}
        </div>
        <p className={`text-sm leading-relaxed ${req.withdrawn ? 'text-fg-tertiary line-through' : 'text-fg'}`}>
          {req.statement}
        </p>
        <p className="mt-1 text-[10px] text-fg-tertiary">
          From {spec.source_title || spec.source_workspace_id}
        </p>
        {req.consumers !== undefined && (
          <p className="mt-0.5 text-[10px] text-fg-tertiary">
            {req.unrouted
              ? 'Routed to: all consumers (unrouted — not yet assigned)'
              : (req.consumers && req.consumers.includes('all'))
                ? 'Routed to: all consumers'
                : (req.consumers && req.consumers.length > 0)
                  ? `Routed to: ${req.consumers.join(', ')}`
                  : 'Routed to: all consumers'}
          </p>
        )}
      </div>

      {/* This build's status */}
      <div>
        <h4 className="text-[10px] font-semibold text-fg-secondary uppercase tracking-wide mb-1">
          This build
        </h4>
        <div className="flex items-center gap-1.5 text-xs">
          {stale ? (
            <>
              <AlertTriangle size={12} className="text-warning flex-shrink-0" strokeWidth={1.5} />
              <span className="text-warning font-medium">Done (stale — spec changed since)</span>
            </>
          ) : done ? (
            <>
              <CheckSquare size={12} className="text-success flex-shrink-0" strokeWidth={1.5} />
              <span className="text-success font-medium">Done</span>
            </>
          ) : (
            <>
              <Square size={12} className="text-fg-tertiary flex-shrink-0" strokeWidth={1.5} />
              <span className="text-fg-secondary">Open</span>
            </>
          )}
          {req.status?.actor && (
            <span className="text-fg-tertiary">
              · by {req.status.actor}
              {req.status.at ? ` · ${formatTimestamp(req.status.at)}` : ''}
            </span>
          )}
        </div>
        {done && req.status?.node_id && (
          <div className="mt-1.5 text-xs text-fg-secondary flex items-center gap-1.5">
            <span className="text-fg-tertiary">Implemented by</span>
            {onNavigateToNode ? (
              <button
                onClick={() => onNavigateToNode(req.status!.node_id!)}
                className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-border-default text-link hover:bg-link/10 transition-colors"
                title="Select this node in the model canvas"
              >
                {req.status.node_id}
              </button>
            ) : (
              <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-bg-warm border border-border-default">
                {req.status.node_id}
              </span>
            )}
          </div>
        )}
        {req.status?.impl_note && (
          <p className="mt-1.5 text-xs text-fg-secondary bg-bg-warm rounded p-2 border border-border-default whitespace-pre-wrap">
            {req.status.impl_note}
          </p>
        )}
      </div>

      {/* Validator verdict — independent of the agent's own done claim */}
      <div>
        <h4 className="text-[10px] font-semibold text-fg-secondary uppercase tracking-wide mb-1">
          Validation
        </h4>
        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium uppercase ${verdictClass}`}>
          {verdict || 'not validated'}
        </span>
      </div>

      {/* Grounding evidence */}
      <div>
        <h4 className="text-[10px] font-semibold text-fg-secondary uppercase tracking-wide mb-1">
          Grounding evidence
        </h4>
        {(req.finding_refs || []).length === 0 ? (
          <p className="text-xs text-fg-tertiary italic">No findings linked to this requirement.</p>
        ) : (
          <ul className="space-y-1">
            {req.finding_refs.map((ref, i) => {
              const resolved = allFindings[ref.finding_id] || null;
              return (
                <li key={`${ref.workspace_id}-${ref.finding_id}-${i}`}>
                  <button
                    onClick={() => resolved && onFindingChipClick(ref.finding_id)}
                    disabled={!resolved}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-colors max-w-full ${
                      resolved
                        ? 'border-link/40 text-link hover:bg-link/10'
                        : 'border-border-default text-fg-tertiary cursor-default'
                    }`}
                    title={
                      ref.citation_excerpt
                        || (resolved
                          ? 'Open finding detail'
                          : 'This finding lives in another workspace and is not loaded in this view')
                    }
                  >
                    <Paperclip size={9} strokeWidth={1.5} className="flex-shrink-0" />
                    <span className="truncate">{findingChipLabel(ref, resolved)}</span>
                  </button>
                  {ref.citation_excerpt && (
                    <p className="mt-0.5 ml-1 text-[10px] text-fg-tertiary italic line-clamp-2">
                      “{ref.citation_excerpt}”
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SpecPanel({
  projectId,
  workspaceId,
  authToken,
  isOpen,
  onClose,
  refreshSignal,
  focusRequirementKey,
  focusNonce,
  onNavigateToNode,
  onDetailOpenChange,
  onPanelResize,
  onSummaryChange,
}: SpecPanelProps) {
  const [specs, setSpecs] = useState<ConsumedSpec[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A requirements read that FAILED, held apart from the approve-action error
  // above. Without it a failed read fell through to `specCount === 0` and
  // rendered "No spec sources — no upstream research workspace is wired into
  // this one", which is a claim about the plan graph made from a read that
  // never completed.
  const [resolutionError, setResolutionError] = useState<SpecResolutionError | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // FAILURE MEMORY — see UnifiedFindingsPanel. Every requirement write bumps
  // refreshSignal, so a 28-requirement sweep would re-read the spec 28 times
  // per mounted panel, each one able to sit for the full read budget against a
  // failing upstream. After a failure only an explicit retry, or a cooled down
  // signal, reads again.
  const lastFailureAt = useRef<number>(0);
  const lastAttemptedRetryNonce = useRef<number>(-1);
  const FAILURE_COOLDOWN_MS = 15_000;
  const [selectedReqKey, setSelectedReqKey] = useState<string | null>(null);
  const [chipFindingId, setChipFindingId] = useState<string | null>(null);
  const [approvingWsId, setApprovingWsId] = useState<string | null>(null);
  const [listWidth, setListWidth] = useState(220);
  const [isResizing, setIsResizing] = useState(false);

  // Grounding findings resolve against whatever the store already holds —
  // the build's dictWorkspace plus the project-level findings map. A ref
  // whose Finding isn't loaded degrades to an inert chip (excerpt only).
  const storeFindings = useProjectStore((s) => s.dictWorkspace?.findings);
  const projectFindings = useProjectStore((s) => s.activeProject?.findings);
  const storeMarkers = useProjectStore((s) => s.dictWorkspace?.markers);
  const allFindings = useMemo(
    () => ({ ...(projectFindings || {}), ...(storeFindings || {}) } as Record<string, DictFinding>),
    [projectFindings, storeFindings],
  );

  // Fetch the joined projection. Runs while closed too, so the parent's
  // toggle badge (via onSummaryChange) stays accurate.
  useEffect(() => {
    const explicitRetry = retryNonce > 0
      && retryNonce !== lastAttemptedRetryNonce.current;
    if (
      !explicitRetry
      && lastFailureAt.current
      && Date.now() - lastFailureAt.current < FAILURE_COOLDOWN_MS
    ) {
      return;
    }
    lastAttemptedRetryNonce.current = retryNonce;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getWorkspaceRequirements(projectId, workspaceId, authToken)
      .then((r) => {
        if (cancelled) return;
        lastFailureAt.current = 0;
        setResolutionError(null);
        setSpecs(r.specs ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        lastFailureAt.current = Date.now();
        // Hold the failure typed, and leave `specs` null so nothing downstream
        // can mistake "unknown" for "none".
        setResolutionError(
          e instanceof SpecResolutionError
            ? e
            : new SpecResolutionError({
                code: 'requirement_spec_resolution_failed',
                status: 0,
                reason: e instanceof Error ? e.message : String(e),
                retriable: true,
                message: 'The requirements for this workspace could not be loaded.',
              }),
        );
        setSpecs(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, workspaceId, authToken, refreshSignal, retryNonce]);

  useEffect(() => {
    if (!onSummaryChange || specs === null || resolutionError) return;
    const openCount = specs.reduce(
      (acc, s) => acc + Math.max(0, s.progress.total - s.progress.done),
      0,
    );
    onSummaryChange({ specCount: specs.length, openCount });
  }, [specs, resolutionError, onSummaryChange]);

  // Controlled selection — a parent can open the panel focused on a specific
  // requirement (NodeInfoBar chip click-through). The nonce re-applies the
  // same key on repeat clicks. The key may land before the specs fetch
  // resolves; the `selected` memo simply resolves it once specs arrive.
  useEffect(() => {
    if (!focusRequirementKey) return;
    setSelectedReqKey(focusRequirementKey);
    setChipFindingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequirementKey, focusNonce]);

  // Selected requirement (composite-keyed — never bare REQ-NNN).
  const selected = useMemo(() => {
    if (!selectedReqKey || !specs) return null;
    for (const spec of specs) {
      for (const section of spec.sections) {
        for (const req of section.requirements) {
          if (reqKey(spec.source_workspace_id, req.requirement_id) === selectedReqKey) {
            return { spec, section: section.section, req };
          }
        }
      }
    }
    return null;
  }, [selectedReqKey, specs]);

  const chipFinding = chipFindingId ? allFindings[chipFindingId] || null : null;
  const isDetailOpen = !!selected;

  useEffect(() => {
    onDetailOpenChange?.(isDetailOpen);
  }, [isDetailOpen, onDetailOpenChange]);

  const handleRequirementClick = useCallback((key: string) => {
    setSelectedReqKey(key);
    setChipFindingId(null);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedReqKey(null);
    setChipFindingId(null);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedReqKey(null);
    setChipFindingId(null);
    onClose();
  }, [onClose]);

  // Human sign-off — optimistic flip, reconciled with the server response
  // (reverted on failure). The endpoint is the only path: no agent tool.
  const handleApprove = useCallback(async (sourceWorkspaceId: string) => {
    setApprovingWsId(sourceWorkspaceId);
    const previous = specs;
    const optimisticAt = new Date().toISOString();
    setSpecs((curr) => (curr || []).map((s) => (
      s.source_workspace_id === sourceWorkspaceId
        ? {
            ...s,
            approval: { approved_by: 'you', approved_at: optimisticAt },
            approval_current: true,
          }
        : s
    )));
    const result = await approveSpec(sourceWorkspaceId, authToken);
    if (result.success) {
      setSpecs((curr) => (curr || []).map((s) => (
        s.source_workspace_id === sourceWorkspaceId
          ? {
              ...s,
              approval: {
                approved_version_hash: result.approved_version_hash,
                approved_by: result.approved_by || 'you',
                approved_at: result.approved_at || optimisticAt,
              },
              approval_current: true,
            }
          : s
      )));
    } else {
      setSpecs(previous);
      setError(result.error || 'Failed to approve spec');
    }
    setApprovingWsId(null);
  }, [specs, authToken]);

  // Left-edge resize handle — sends incremental pixel deltas to parent
  const handlePanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    let lastX = e.clientX;
    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - lastX;
      lastX = ev.clientX;
      onPanelResize?.(delta);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onPanelResize]);

  if (!isOpen) return null;

  const specCount = specs?.length ?? 0;

  return (
    <div className="h-full bg-surface border-l border-border-default shadow-sm flex overflow-hidden relative">
      {/* Left-edge resize handle */}
      {onPanelResize && (
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 transition-colors ${
            isResizing ? 'bg-accent' : 'bg-transparent hover:bg-accent-tint'
          }`}
          onMouseDown={handlePanelResizeStart}
          title="Drag to resize"
        />
      )}

      {/* Spec list */}
      <div
        className="flex-shrink-0 border-r border-border-default flex flex-col h-full"
        style={{ width: isDetailOpen ? listWidth : '100%' }}
      >
        <div className="px-2 py-1.5 flex items-center justify-between border-b border-divider bg-bg-warm flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <ListChecks size={14} className="text-accent" />
            <span className="text-xs font-semibold text-fg-secondary">Specs</span>
            <span className="text-[10px] px-1 py-0.5 rounded-full bg-accent-tint text-accent-hover">
              {specCount}
            </span>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-bg-warm rounded" title="Close panel">
            <X size={14} className="text-fg-tertiary" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-1.5 space-y-2">
          {error && (
            <div className="mx-0.5 p-2 rounded bg-danger-bg border border-danger text-[11px] text-danger">
              {error}
            </div>
          )}
          {loading && specs === null ? (
            <p className="text-center py-12 text-sm text-fg-tertiary">Loading specs…</p>
          ) : resolutionError ? (
            <div
              className="mx-0.5 rounded border border-warning bg-warning-bg p-3"
              data-testid="spec-resolution-error"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  size={14}
                  strokeWidth={1.8}
                  className="mt-0.5 flex-shrink-0 text-warning"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-warning-ink">
                    The requirements could not be read
                  </div>
                  <div className="mt-1 text-[11px] leading-relaxed text-fg-secondary">
                    This is not an empty spec — the upstream research workspace
                    could not be reached, so what this build has to satisfy is
                    unknown.
                  </div>
                  {resolutionError.reason && (
                    <div className="mt-1 break-words font-mono text-[10px] text-fg-tertiary">
                      {resolutionError.reason}
                    </div>
                  )}
                  {resolutionError.workspaces.length > 0 && (
                    <div className="mt-1 break-words text-[10px] text-fg-tertiary">
                      Could not read: {resolutionError.workspaces.join(', ')}
                    </div>
                  )}
                  {resolutionError.retriable ? (
                    <button
                      type="button"
                      onClick={() => setRetryNonce((n) => n + 1)}
                      data-testid="spec-resolution-retry"
                      className="mt-2 inline-flex items-center gap-1 rounded border border-border-default bg-surface px-2 py-1 text-[11px] text-fg hover:bg-primary-tint"
                    >
                      <RefreshCw size={11} strokeWidth={1.8} />
                      Try again
                    </button>
                  ) : (
                    <div className="mt-2 text-[10px] text-fg-tertiary">
                      Retrying will not help — this needs an operator.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : specCount === 0 ? (
            <div className="text-center py-12">
              <ListChecks size={32} className="mx-auto mb-3 text-fg-secondary" />
              <p className="text-sm text-fg-tertiary">No spec sources</p>
              <p className="mt-1 text-[11px] text-fg-tertiary px-3">
                No upstream research workspace is wired into this one in the
                plan. Specs flow along the plan graph's incoming edges.
              </p>
            </div>
          ) : (
            specs!.map((spec) => (
              <SpecGroup
                key={spec.source_workspace_id}
                spec={spec}
                selectedKey={selectedReqKey}
                approving={approvingWsId === spec.source_workspace_id}
                onRequirementClick={handleRequirementClick}
                onApprove={handleApprove}
              />
            ))
          )}
        </div>
      </div>

      {/* List/detail resize handle */}
      {isDetailOpen && (
        <div
          className="w-1 flex-shrink-0 cursor-col-resize hover:bg-accent-tint transition-colors"
          onMouseDown={(e) => {
            e.preventDefault();
            let lastX = e.clientX;
            const onMove = (ev: MouseEvent) => {
              const delta = ev.clientX - lastX;
              lastX = ev.clientX;
              setListWidth((w) => Math.max(140, Math.min(360, w + delta)));
            };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />
      )}

      {/* Detail — extension of the list, same panel */}
      {selected && !chipFinding && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button
              onClick={handleBackToList}
              className="p-1 hover:bg-bg-warm rounded transition-colors"
              title="Back to list only"
            >
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">Requirement detail</h3>
          </div>
          <RequirementDetail
            spec={selected.spec}
            section={selected.section}
            req={selected.req}
            allFindings={allFindings}
            onFindingChipClick={setChipFindingId}
            onNavigateToNode={onNavigateToNode}
          />
        </div>
      )}

      {/* Grounding-finding detail — the chip's ref points at a real Finding,
          so the existing FindingDetailView renders it for free. */}
      {selected && chipFinding && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button
              onClick={() => setChipFindingId(null)}
              className="p-1 hover:bg-bg-warm rounded transition-colors"
              title={`Back to ${selected.req.requirement_id}`}
            >
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">
              Finding detail
              <span className="ml-2 font-mono text-[10px] text-fg-tertiary font-normal">
                grounds {selected.req.requirement_id}
              </span>
            </h3>
          </div>
          <FindingDetailView
            finding={chipFinding}
            allFindings={allFindings}
            allMarkers={storeMarkers}
            onFindingClick={(id: string) => {
              if (allFindings[id]) setChipFindingId(id);
            }}
          />
        </div>
      )}
    </div>
  );
}
